import type { ForwardedRef } from 'react';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useImperativeHandle, useMemo } from 'react';
import { useNavigation } from 'react-router';
import { useEventListener } from 'usehooks-ts';

import { useStoreSelector } from '~/plugins/store/hooks/selector';

import type { AppRouterContainerHooks, AppRouterContainerProps, AppRouterContainerRef } from './_.type';

/**
 * Use AppRouterContainer hooks.
 *
 * @param {AppRouterContainerProps} _props AppRouterContainer props.
 * @param {AppRouterContainerRef} ref AppRouterContainer ref.
 *
 * @returns {AppRouterContainerHooks} AppRouterContainer hooks.
 */
export default function useAppRouterContainerHooks(_props: AppRouterContainerProps, ref: ForwardedRef<AppRouterContainerRef>): AppRouterContainerHooks {
  const navigation = useNavigation();

  const isLoading = useMemo(() => navigation.state !== 'idle', [navigation.state]);

  const sessions = useStoreSelector((state) => state.feature_process.sessionManager.sessions);

  const onBeforeUnloadWindow = useCallback<Func<[event: Event]>>(
    (event) => {
      const isInProgress = sessions
        .map(({ children }) => children)
        .flat()
        .some(({ status }) => status === 'scanning' || status === 'uploading');

      if (isInProgress) {
        event.preventDefault();
      }
    },
    [sessions],
  );

  useEventListener('beforeunload', onBeforeUnloadWindow);

  useImperativeHandle(ref, () => ({}));

  return useMemo<AppRouterContainerHooks>(
    () => ({
      refs: {},

      states: {
        isLoading,
      },

      events: {},
    }),
    [isLoading],
  );
}
